import { useEffect, useState, useCallback } from "react";
import api, { downloadUrl, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import logger from "../../lib/logger";
import { Plus, X, Trash2, ChevronDown, ChevronUp, Download } from "lucide-react";

let _qSeq = 0;
const newUid = () => `q_${Date.now()}_${++_qSeq}`;
const blankQ = () => ({ _uid: newUid(), question: "", options: ["", ""], _optUids: [newUid(), newUid()], correct_index: 0 });

function SubmittedStatus({ exam }) {
  if (exam.exam_type === "mc") {
    return <p className="mt-2">Score: <span className="font-bold">{exam.submission.score}/100</span> ({exam.submission.correct}/{exam.submission.total} correct)</p>;
  }
  if (exam.submission.graded) {
    return <p className="mt-2">Grade: <span className="font-bold">{exam.submission.grade}/100</span> {exam.submission.feedback && <span className="italic">&ldquo;{exam.submission.feedback}&rdquo;</span>}</p>;
  }
  return <p className="mt-2 text-[var(--muted-text)]">Awaiting grade.</p>;
}

function StudentExamStatus({ exam, onStart, onSubmitFile }) {
  if (exam.submission) {
    return (
      <div className="text-sm">
        <p><span className="tag tag-mint">Submitted</span></p>
        <SubmittedStatus exam={exam} />
      </div>
    );
  }
  if (exam.exam_type === "mc") {
    return <button data-testid={`start-exam-${exam.id}`} onClick={() => onStart(exam)} className="nb-btn nb-btn-yellow text-sm">Start exam</button>;
  }
  return (
    <label className="nb-btn nb-btn-yellow text-sm cursor-pointer inline-flex items-center gap-2">
      Submit file
      <input data-testid={`exam-file-submit-${exam.id}`} type="file" className="hidden" onChange={(e) => onSubmitFile(exam.id, e.target)} />
    </label>
  );
}

export default function ExamsTab({ courseId }) {
  const { user } = useAuth();
  const isInstructor = user?.role === "instructor";
  const [exams, setExams] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", exam_type: "mc", due_date: "", questions: [blankQ()] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [activeExam, setActiveExam] = useState(null); // student taking
  const [answers, setAnswers] = useState({});
  const [openSubs, setOpenSubs] = useState({});
  const [subMap, setSubMap] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/courses/${courseId}/exams`);
      setExams(res.data);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const body = { course_id: courseId, title: form.title, description: form.description, exam_type: form.exam_type, due_date: form.due_date || null };
      if (form.exam_type === "mc") body.questions = form.questions.map((q) => ({ question: q.question, options: q.options, correct_index: q.correct_index }));
      await api.post("/exams", body);
      setShowNew(false);
      setForm({ title: "", description: "", exam_type: "mc", due_date: "", questions: [blankQ()] });
      load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const submitMC = async (exam) => {
    const answerArr = exam.questions.map((_, i) => answers[i] ?? -1);
    try {
      const res = await api.post(`/exams/${exam.id}/submit`, { exam_id: exam.id, answers: answerArr });
      alert(`Submitted! Score: ${res.data.score}/100 (${res.data.correct}/${res.data.total} correct)`);
      setActiveExam(null); setAnswers({});
      load();
    } catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const submitFile = async (examId, fileEl) => {
    const file = fileEl.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/exams/${examId}/submit-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      fileEl.value = "";
      load();
    } catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this exam?")) return;
    try { await api.delete(`/exams/${id}`); load(); } catch (e) { logger.error("Delete exam failed:", e); }
  };

  const toggleSubs = async (eid) => {
    const open = !openSubs[eid];
    setOpenSubs((s) => ({ ...s, [eid]: open }));
    if (open) {
      try {
        const res = await api.get(`/exams/${eid}/submissions`);
        setSubMap((s) => ({ ...s, [eid]: res.data }));
      } catch (e) {
        logger.error("Failed to load exam submissions:", e);
      }
    }
  };

  const gradeSub = async (subId, eid) => {
    const grade = parseFloat(window.prompt("Grade (0-100):", "85"));
    if (isNaN(grade)) return;
    const feedback = window.prompt("Feedback (optional):", "") || "";
    try {
      await api.post(`/exam-submissions/${subId}/grade`, { grade, feedback });
      const res = await api.get(`/exams/${eid}/submissions`);
      setSubMap((s) => ({ ...s, [eid]: res.data }));
    } catch (e) { alert(e.message); }
  };

  // Question editor helpers
  const setQ = (qi, patch) => setForm((f) => ({ ...f, questions: f.questions.map((q, i) => i === qi ? { ...q, ...patch } : q) }));
  const setOpt = (qi, oi, v) => setForm((f) => ({ ...f, questions: f.questions.map((q, i) => i === qi ? { ...q, options: q.options.map((o, j) => j === oi ? v : o) } : q) }));
  const addOpt = (qi) => setForm((f) => ({ ...f, questions: f.questions.map((q, i) => i === qi ? { ...q, options: [...q.options, ""], _optUids: [...q._optUids, newUid()] } : q) }));
  const addQ = () => setForm((f) => ({ ...f, questions: [...f.questions, blankQ()] }));
  const rmQ = (qi) => setForm((f) => ({ ...f, questions: f.questions.filter((_, i) => i !== qi) }));

  if (activeExam) {
    return (
      <div className="nb-card-static p-6 space-y-5 animate-pop">
        <div className="flex justify-between items-start">
          <div>
            <span className="tag tag-pink mb-2 inline-block">Taking exam</span>
            <h2 className="font-display text-2xl font-bold">{activeExam.title}</h2>
            <p className="text-sm text-[var(--muted-text)]">{activeExam.description}</p>
          </div>
          <button onClick={() => { setActiveExam(null); setAnswers({}); }} className="nb-btn nb-btn-white text-sm">Cancel</button>
        </div>
        {activeExam.questions.map((q, qi) => (
          <div key={`q-${qi}-${(q.question || "").slice(0, 24)}`} className="border-2 border-[var(--gold)] rounded-xl p-4 bg-[var(--paper)]">
            <p className="font-semibold mb-3">{qi + 1}. {q.question}</p>
            <div className="space-y-2">
              {q.options.map((opt, oi) => (
                <label key={`o-${qi}-${oi}-${(opt || "").slice(0, 16)}`} className={`flex items-center gap-3 p-2 rounded border-2 border-[var(--gold)] cursor-pointer ${answers[qi] === oi ? "bg-[var(--gold)] text-[var(--navy-on-gold)]" : "bg-[var(--paper-soft)] text-[var(--gold-soft)] hover:bg-[var(--paper)]"}`}>
                  <input data-testid={`exam-q${qi}-opt${oi}`} type="radio" name={`q${qi}`} checked={answers[qi] === oi} onChange={() => setAnswers({ ...answers, [qi]: oi })} />
                  <span className="text-sm">{opt}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
        <button data-testid="exam-submit-btn" onClick={() => submitMC(activeExam)} className="nb-btn nb-btn-yellow">Submit exam</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isInstructor && (
        <button data-testid="new-exam-btn" onClick={() => setShowNew(true)} className="nb-btn nb-btn-pink flex items-center gap-2"><Plus className="w-4 h-4" /> New exam</button>
      )}

      {exams.length === 0 && <div className="nb-card-static p-5 text-sm text-[var(--muted-text)]">No exams yet.</div>}

      {exams.map((ex) => (
        <div key={ex.id} data-testid={`exam-${ex.id}`} className="nb-card-static p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-display text-xl font-bold">{ex.title}</h3>
                <span className="tag tag-blue">{ex.exam_type === "mc" ? "Multiple choice" : "File submission"}</span>
              </div>
              {ex.due_date && <p className="text-xs text-[var(--muted-text)]">Due {ex.due_date}</p>}
              <p className="text-sm mt-2">{ex.description}</p>
              {ex.exam_type === "mc" && <p className="text-xs text-[var(--muted-text)] mt-1">{ex.questions?.length || 0} question(s)</p>}
            </div>
            {isInstructor && (
              <button data-testid={`delete-exam-${ex.id}`} onClick={() => remove(ex.id)} className="nb-btn nb-btn-white text-sm"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>

          {!isInstructor && (
            <div className="mt-4 border-t-2 border-[var(--gold)]/10 pt-4">
              <StudentExamStatus
                exam={ex}
                onStart={(e) => { setActiveExam(e); setAnswers({}); }}
                onSubmitFile={submitFile}
              />
            </div>
          )}

          {isInstructor && (
            <div className="mt-4 border-t-2 border-[var(--gold)]/10 pt-3">
              <button onClick={() => toggleSubs(ex.id)} className="text-sm font-semibold flex items-center gap-1">
                Submissions {openSubs[ex.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {openSubs[ex.id] && (
                <ul className="mt-3 space-y-2">
                  {(subMap[ex.id] || []).length === 0 && <li className="text-sm text-[var(--muted-text)]">No submissions yet.</li>}
                  {(subMap[ex.id] || []).map((s) => (
                    <li key={s.id} className="flex items-center justify-between border-2 border-[var(--gold)] rounded-lg px-3 py-2 bg-[var(--paper)]">
                      <div className="text-sm">
                        <p className="font-semibold">{s.user_name}</p>
                        {ex.exam_type === "mc" ? (
                          <p className="text-xs">Score: <span className="font-bold">{s.score}/100</span> · {s.correct}/{s.total}</p>
                        ) : (
                          <p className="text-xs text-[var(--muted-text)]">{s.filename} · {new Date(s.submitted_at).toLocaleString()} {s.graded && <span className="ml-2">Grade: {s.grade}/100</span>}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {ex.exam_type === "file" && (
                          <>
                            <a href={downloadUrl(`/exam-submissions/${s.id}/download`)} target="_blank" rel="noreferrer" className="nb-btn nb-btn-white text-xs"><Download className="w-3.5 h-3.5" /></a>
                            <button onClick={() => gradeSub(s.id, ex.id)} className="nb-btn nb-btn-mint text-xs">{s.graded ? "Regrade" : "Grade"}</button>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      ))}

      {showNew && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-8 overflow-y-auto" onClick={() => setShowNew(false)}>
          <div className="nb-card-static p-7 w-full max-w-2xl animate-pop" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl font-bold">New exam</h2>
              <button onClick={() => setShowNew(false)} className="w-9 h-9 border-2 border-[var(--gold)] rounded-lg flex items-center justify-center hover:bg-[var(--yellow)]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <input data-testid="exam-title-input" required placeholder="Exam title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="nb-input" />
                <select data-testid="exam-type-select" value={form.exam_type} onChange={(e) => setForm({ ...form, exam_type: e.target.value })} className="nb-input">
                  <option value="mc">Multiple choice (auto-graded)</option>
                  <option value="file">File submission</option>
                </select>
              </div>
              <textarea data-testid="exam-desc-input" placeholder="Description / instructions" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="nb-input min-h-[80px]" />
              <input data-testid="exam-due-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="nb-input" />

              {form.exam_type === "mc" && (
                <div className="space-y-4 border-t-2 border-[var(--gold)]/10 pt-4">
                  <h3 className="font-display font-bold">Questions</h3>
                  {form.questions.map((q, qi) => (
                    <div key={q._uid} className="border-2 border-[var(--gold)] rounded-xl p-3 space-y-2">
                      <div className="flex gap-2 items-start">
                        <input required placeholder={`Question ${qi + 1}`} value={q.question} onChange={(e) => setQ(qi, { question: e.target.value })} className="nb-input flex-1" />
                        {form.questions.length > 1 && <button type="button" onClick={() => rmQ(qi)} className="nb-btn nb-btn-white text-xs"><Trash2 className="w-3.5 h-3.5" /></button>}
                      </div>
                      {q.options.map((opt, oi) => (
                        <div key={q._optUids[oi]} className="flex gap-2 items-center">
                          <input type="radio" checked={q.correct_index === oi} onChange={() => setQ(qi, { correct_index: oi })} title="Correct answer" />
                          <input required placeholder={`Option ${oi + 1}`} value={opt} onChange={(e) => setOpt(qi, oi, e.target.value)} className="nb-input flex-1" />
                        </div>
                      ))}
                      <button type="button" onClick={() => addOpt(qi)} className="text-xs font-semibold underline">+ Add option</button>
                    </div>
                  ))}
                  <button type="button" onClick={addQ} className="nb-btn nb-btn-white text-sm">+ Add question</button>
                </div>
              )}

              {error && <div className="text-sm text-red-600">{error}</div>}
              <button data-testid="exam-create-btn" disabled={busy} type="submit" className="nb-btn nb-btn-pink w-full">{busy ? "Creating..." : "Create exam"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
